import {
  AirshipThemeOption,
  getAirshipThemeColors,
  QuoteContext,
  RewriteOption,
  useAIGenerator,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  AIEditOptions,
  FlexBox,
  ItemSelector,
  LoadingButton,
  SanitizedTrimmedHtml,
  SortOrderController,
  SortOrderFormSchema,
} from '@airship-platform/jam-web/components';
import { HTMLEditor } from '@airship-platform/jam-web/components-x';
import {
  AirshipThemeIcon,
  CheckmarkIcon,
  LeftArrowIcon,
  PlusIcon,
} from '@airship-platform/jam-web/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FormProvider,
  useForm,
} from 'react-hook-form';
import { z } from 'zod';
import PresentationHeaderInputs, {
  PresentationHeaderFormSchema,
} from '../edit-presentation-section/PresentationHeaderInputs';
import EditPresentationUpload from '../media-section/EditPresentationUpload';
import ProposalSummaryModal from '../modals/ProposalSummaryModal';

const formSchema = z
  .object({
    summary: z.string().max(10_000).optional(),
    theme: z.enum(['AIRSHIP_RED', 'AIRSHIP_BLUE', 'AIRSHIP_GOLD']),
    coverPhoto: z
      .object({
        fileType: z.enum([
          'ORG_PROPOSAL_HEADER',
          'USER_UPLOAD',
          'AIRSHIP_DEFAULT',
          'NO_IMAGE',
        ]),
        fileId: z.uuid().optional(),
      })
      .optional(),
    priceDisplayMode: z.enum([
      'TOTAL_COST',
      'FINANCING_PAYMENT',
      'FINANCING_AND_TOTAL_COST',
    ]),
    showAIOptionsSummary: z.boolean(),
  })
  .merge(SortOrderFormSchema)
  .merge(PresentationHeaderFormSchema);

export type EditPresentationFormInputs = z.infer<typeof formSchema>;

const themeOptions: Record<AirshipThemeOption, { title: string }> = {
  AIRSHIP_BLUE: {
    title: 'Airship Blue',
  },
  AIRSHIP_RED: {
    title: 'Airship Red',
  },
  AIRSHIP_GOLD: {
    title: 'Airship Gold',
  },
};

type EditPresentationSectionProps = {
  appointmentId: string;
  organizationId: string;
  defaultValues: EditPresentationFormInputs;
  formId?: string;
  onSubmit: (data: EditPresentationFormInputs) => void;
};

const EditPresentationSection: React.FC<EditPresentationSectionProps> = ({
  defaultValues,
  appointmentId,
  organizationId,
  formId,
  onSubmit,
}) => {
  const { showToast } = useToast();

  const { isLoadingOption: isGenerating, generateQuote } = useAIGenerator();

  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [previousSummary, setPreviousSummary] = useState<string | null>(null);
  const [isProposalSummaryModalOpen, setIsProposalSummaryModalOpen] =
    useState(false);

  const { control, handleSubmit, watch, ...presentationForm } =
    useForm<EditPresentationFormInputs>({
      resolver: zodResolver(formSchema),
      defaultValues,
    });

  const summary = watch('summary') || '';

  const improveSummaryDescriptionHandler = async (
    field: ControllerRenderProps<EditPresentationFormInputs, 'summary'>,
    rewriteOption: RewriteOption
  ) => {
    const summary = field.value;

    if (!summary) {
      showToast('Please add a summary to improve', 'info');
      return;
    }
    const result = await generateQuote(
      summary,
      rewriteOption,
      QuoteContext.PROPOSAL_SUMMARY,
      {
        organizationId,
        appointmentId,
      }
    );
    if (result.length) {
      setPreviousSummary(summary);
      field.onChange(result.map((r) => r.improvedQuote).join(' '));
      showToast('Your description has been improved!', 'success');
    } else {
      showToast(
        'We were unable to generate an improved description',
        'warning'
      );
    }
  };

  const handleUndo = (
    field: ControllerRenderProps<EditPresentationFormInputs, 'summary'>
  ) => {
    if (previousSummary) {
      field.onChange(previousSummary);
      setPreviousSummary(null);
      showToast('Update reverted', 'info');
    }
  };

  return (
    <Box
      component={'form'}
      id={formId}
      onSubmit={handleSubmit(onSubmit)}
      sx={{ width: '100%' }}
    >
      <FormProvider
        watch={watch}
        control={control}
        handleSubmit={handleSubmit}
        {...presentationForm}
      >
        <FlexBox flexDirection={'column'} gap={2}>
          <PresentationHeaderInputs />
          <Divider />
          <Controller
            name={'summary'}
            control={control}
            render={({ field }) => (
              <FlexBox flexDirection="column" gap={2}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Proposal summary
                </Typography>
                {field.value === undefined ||
                  (field.value === '' && (
                    <Typography variant="body3">
                      Summarize your proposal and help guide the customer to the
                      best option.
                    </Typography>
                  ))}
                {!isEditingSummary && (
                  <SanitizedTrimmedHtml
                    maxLength={100}
                    variant="body3"
                    html={field.value || ''}
                    onSeeMore={() => setIsProposalSummaryModalOpen(true)}
                  />
                )}
                <FlexBox gap={2}>
                  {isEditingSummary && (
                    <IconButton
                      onClick={() => setIsEditingSummary(false)}
                      color="softGold"
                    >
                      <CheckmarkIcon />
                    </IconButton>
                  )}
                  {!isEditingSummary && (
                    <Button
                      variant="contained"
                      color="softGold"
                      startIcon={<PlusIcon />}
                      onClick={() => setIsEditingSummary(true)}
                    >
                      {field.value === undefined || field.value === ''
                        ? 'Add'
                        : 'Edit'}{' '}
                      summary
                    </Button>
                  )}
                </FlexBox>
                {isEditingSummary && (
                  <FlexBox flexDirection="column" gap={2}>
                    <HTMLEditor
                      disabled={!!isGenerating}
                      value={field.value || ''}
                      plugins={['wordcount']}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                    />
                    <FlexBox gap={2} flexWrap="wrap" mt={2}>
                      <AIEditOptions
                        onClick={(rewriteOption) =>
                          improveSummaryDescriptionHandler(field, rewriteOption)
                        }
                        disabled={!!isGenerating}
                        isLoadingOption={isGenerating}
                      />
                      <LoadingButton
                        color="softGold"
                        startIcon={<LeftArrowIcon />}
                        onClick={() => handleUndo(field)}
                        disabled={!!isGenerating || !previousSummary}
                        loading={false}
                      >
                        Undo update
                      </LoadingButton>
                    </FlexBox>
                  </FlexBox>
                )}
              </FlexBox>
            )}
          />
          <Divider />
          <Controller
            name={'priceDisplayMode'}
            control={control}
            render={({ field }) => (
              <FlexBox gap={2}>
                <ItemSelector
                  label="Full price"
                  value="TOTAL_COST"
                  checked={field.value === 'TOTAL_COST'}
                  type="radio"
                  fullWidth
                  handleChange={() => field.onChange('TOTAL_COST')}
                />
                <ItemSelector
                  label="Financing price"
                  value="FINANCING_PAYMENT"
                  checked={field.value === 'FINANCING_PAYMENT'}
                  type="radio"
                  fullWidth
                  handleChange={() => field.onChange('FINANCING_PAYMENT')}
                />
                <ItemSelector
                  label="Show both"
                  value="FINANCING_AND_TOTAL_COST"
                  checked={field.value === 'FINANCING_AND_TOTAL_COST'}
                  type="radio"
                  fullWidth
                  handleChange={() =>
                    field.onChange('FINANCING_AND_TOTAL_COST')
                  }
                />
              </FlexBox>
            )}
          />
          <Divider />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Item ordering
          </Typography>
          <SortOrderController flexDirection="column" gap={4} />
          <Divider />
          <Controller
            name="showAIOptionsSummary"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FlexBox alignItems="center" justifyContent="space-between">
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Show AI Options Summary
                </Typography>
                <FlexBox gap={1} alignItems="center">
                  <Switch
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    color="tertiary"
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: (theme) => theme.palette.blue?.[700],
                      fontWeight: 500,
                    }}
                  >
                    Show
                  </Typography>
                </FlexBox>
              </FlexBox>
            )}
          />
          <Controller
            name="theme"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FlexBox flexDirection="column" gap={2}>
                <Typography variant="body1">Choose theme</Typography>
                <FlexBox gap={2}>
                  {Object.entries(themeOptions).map(([key, option]) => {
                    const isSelected = key === value;
                    return (
                      <FlexBox
                        key={key}
                        flex={1}
                        p={2}
                        border={(theme) =>
                          isSelected
                            ? `2px solid ${theme.palette.blue?.[400]}`
                            : `1px solid ${theme.palette.gray?.[300]}`
                        }
                        bgcolor={(theme) =>
                          isSelected
                            ? theme.palette.blue?.[50]
                            : theme.palette.background.paper
                        }
                        borderRadius={2}
                        cursor={'pointer'}
                        onClick={() => onChange(key as AirshipThemeOption)}
                        flexDirection={'column'}
                        alignItems={'center'}
                        gap={1}
                      >
                        <AirshipThemeIcon
                          {...getAirshipThemeColors(key as AirshipThemeOption)}
                          width="4rem"
                          height="4rem"
                        />
                        <Typography variant={'body1'} align="center">
                          {option.title}
                        </Typography>
                      </FlexBox>
                    );
                  })}
                </FlexBox>
              </FlexBox>
            )}
          />
          <Controller
            name="coverPhoto"
            control={control}
            render={({ field: { onChange, value } }) => (
              <EditPresentationUpload
                value={value?.fileId}
                onChange={onChange}
              />
            )}
          />
        </FlexBox>
        <ProposalSummaryModal
          open={isProposalSummaryModalOpen}
          onClose={() => setIsProposalSummaryModalOpen(false)}
          summary={summary}
        />
      </FormProvider>
    </Box>
  );
};

export default EditPresentationSection;
